import { defineStore, acceptHMRUpdate } from 'pinia'
import $ from 'jquery'
import type { SessionOrganizationType } from '@/types/session-organization.type'
import type { SessionVenueType } from '@/types/session-venue.type'
import type { SessionUserType } from '@/types/session-user.type'
import { UserPermissionRole } from '../types/user-permissions.type'
import type { UserPermissionsEnum } from '@/enums/user-permissions.enum'
import { PlansEnum } from '../types/subscription.type'
import { LOCAL_STORAGE_KEYS, LocalStorageService } from '../services/LocalStorageService'

interface State {
  user: SessionUserType
  currentVenue: SessionVenueType
  editItemId: string
  editItemMasterId: string
  editItemVipProductId: string
  editRecipeId: string
  currentLocation?: any
  referer: string
  editOrderId: string
  venues: { id: number; name: string }[]
  organizations: { id: number; name: string }[]
  currentOrganization: SessionOrganizationType
  impersonatorId: number | null
}

export const useUserStore = defineStore('user', {
  state: (): State => {
    return {
      user: {} as SessionUserType,
      currentVenue: {} as SessionVenueType,
      editItemId: '',
      editItemMasterId: '',
      editItemVipProductId: '',
      editRecipeId: '',
      currentLocation: {},
      referer: '',
      editOrderId: '',
      venues: [],
      organizations: [],
      currentOrganization: {} as SessionOrganizationType,
      impersonatorId: null
    }
  },
  getters: {
    hasPermission(state) {
      return (permission: string): boolean => {
        return state.user?.permissions?.[permission]
      }
    },
    isAdmin(state): boolean {
      return (
        state.user.permissions.role === UserPermissionRole.ADMIN ||
        state.user.permissions.role === UserPermissionRole.SUPER_ADMIN
      )
    },
    currentPlan(state): number {
      return state.currentOrganization.plan
    },
    isBasicPlan(state): boolean {
      return state.currentOrganization.plan === PlansEnum.Basic
    },
    isCurrentVenueVerified(state): boolean {
      return state.currentVenue.vip_verification_status === 'VERIFIED'
    }
  },
  actions: {
    async getUser() {
      const data = await $.get('/whoami')
      const {
        currentLocation,
        currentOrganization,
        currentVenue,
        editItemId,
        editItemMasterId,
        editItemVipProductId,
        editOrderId,
        editRecipeId,
        organizations,
        referrer,
        user,
        venues
      } = data
      this.currentLocation = currentLocation
      this.currentOrganization = currentOrganization
      this.currentVenue = currentVenue
      this.editItemId = editItemId
      this.editItemMasterId = editItemMasterId
      this.editItemVipProductId = editItemVipProductId
      this.editOrderId = editOrderId
      this.editRecipeId = editRecipeId
      this.organizations = organizations
      this.referer = referrer
      this.user = user
      this.venues = venues
    },
    async clearSessionKeys() {
      LocalStorageService.removeItem(LOCAL_STORAGE_KEYS.DASHBOARD_BANNER_TRIAL_PAYMENT_MISSING)
    },
    async logoutUser() {
      let newImpersonatorId: number | null = null
      try {
        const res = await $.get('/logout')
        newImpersonatorId = res?.id ?? null
      } catch (error) {
        console.error('Logout API failed, but clearing local session anyway.', error)
      } finally {
        const activeStores = (this.$pinia as any)?._s

        if (activeStores) {
          activeStores.forEach((store: any) => store.$reset?.())
        }
        if (newImpersonatorId) {
          this.impersonatorId = newImpersonatorId
        }
      }
    },
    currentUserHasPermission(permission: UserPermissionsEnum, requiredPlan = 0) {
      return (
        (this.currentOrganization?.plan >= requiredPlan &&
          !!this.user?.permissions?.[permission]) ||
        this.user.user_level === 9
      )
    }
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useUserStore, import.meta.hot))
}
